import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Calendar, TrendingUp, AlertCircle, X, Tag } from 'lucide-react';
import { adPackageApi, userProfileApi } from '../services/api';
import { AdPackage, AdPurchaseCreate, Vendor, UserProfile } from '../types';
import { useVendor } from '../contexts/VendorContext';
import { calculateDiscountedPrice, formatPrice } from '../utils/pricing';
import { extractApiError } from '../utils/errorUtils';

export default function VendorAdPurchase() {
  const navigate = useNavigate();
  const { vendors } = useVendor();
  const [packages, setPackages] = useState<AdPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<AdPackage | null>(null);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    loadPackages();
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const profile = await userProfileApi.getProfile();
      setUserProfile(profile);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load user profile:', apiError);
      // Non-blocking error - discount just won't show if profile fails to load
    }
  };

  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await adPackageApi.getPackages({ is_active: true });
      setPackages(response.items);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load packages:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const seasonEmojis: Record<string, string> = {
    spring: '🌸',
    summer: '☀️',
    fall: '🍂',
    winter: '❄️',
    year_round: '📅'
  };

  const seasonLabels: Record<string, string> = {
    spring: 'Spring Special',
    summer: 'Summer Special',
    fall: 'Fall Special',
    winter: 'Winter Special',
    year_round: 'Available Year-Round'
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Purchase Advertisement</h1>
        <p className="text-gray-600 mt-2">
          Choose a package and promote your vendor profile to potential customers
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
            <button
              type="button"
              onClick={loadPackages}
              className="text-sm text-red-600 hover:text-red-800 underline mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Free Ad Credits Banner - Issue #203 / #220 */}
      {(userProfile?.free_ad_credits ?? 0) > 0 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200 rounded-lg">
          <div className="flex items-start">
            <Tag className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-1">
                🎉 You have {userProfile.free_ad_credits} free ad credit{userProfile.free_ad_credits > 1 ? 's' : ''} remaining!
              </h3>
              <p className="text-sm text-green-700">
                Your free credits will be automatically applied at checkout.
                {userProfile.free_ad_credits === 1 ? ' This credit can be used for one advertisement purchase.' : ` These credits can be used for ${userProfile.free_ad_credits} advertisement purchases.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading packages...</p>
        </div>
      )}

      {/* Packages Grid */}
      {!loading && packages.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-primary transition-colors relative"
            >
              {/* Season Badge */}
              {pkg.season && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-moonbone text-ink">
                    {seasonEmojis[pkg.season]} {seasonLabels[pkg.season]}
                  </span>
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                {pkg.description && (
                  <p className="text-sm text-gray-600">{pkg.description}</p>
                )}
              </div>

              {/* Price */}
              <div className="mb-4">
                {userProfile?.ad_discount_percent && userProfile.ad_discount_percent > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Tag className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-600">
                        {userProfile.ad_discount_percent}% Early Access Discount
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl text-gray-400 line-through">
                        {formatPrice(pkg.price_cents, pkg.currency)}
                      </span>
                      <span className="text-4xl font-bold text-green-600">
                        {formatPrice(calculateDiscountedPrice(pkg.price_cents, userProfile.ad_discount_percent), pkg.currency)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">
                      {formatPrice(pkg.price_cents, pkg.currency)}
                    </span>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span>{pkg.duration_days} days of advertising</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <TrendingUp className="h-4 w-4 text-secondary mr-2 flex-shrink-0" />
                  <span>+{pkg.priority_boost} priority boost</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Package className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span>Up to {pkg.max_ads_per_purchase} ad{pkg.max_ads_per_purchase > 1 ? 's' : ''}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSelectedPackage(pkg);
                  setShowPurchaseForm(true);
                }}
                className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark font-medium"
              >
                Select Package
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && packages.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No advertisement packages available at this time</p>
        </div>
      )}

      {/* Purchase Form Modal */}
      {showPurchaseForm && selectedPackage && (
        <PurchaseFormModal
          package={selectedPackage}
          vendors={vendors}
          onClose={() => {
            setShowPurchaseForm(false);
            setSelectedPackage(null);
          }}
          onSuccess={() => {
            setShowPurchaseForm(false);
            setSelectedPackage(null);
            navigate('/vendor/ad-purchases');
          }}
        />
      )}
    </div>
  );
}

// Purchase Form Modal Component
interface PurchaseFormModalProps {
  package: AdPackage;
  vendors: Vendor[];
  onClose: () => void;
  onSuccess: () => void;
}

function PurchaseFormModal({ package: pkg, vendors, onClose, onSuccess }: PurchaseFormModalProps) {
  const [formData, setFormData] = useState<AdPurchaseCreate>({
    package_id: pkg.id,
    vendor_id: vendors.length > 0 ? vendors[0].id : undefined,
    title: '',
    description: '',
    ad_type: 'custom',
    target_url: '',
    image_url: '',
    display_text: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await adPackageApi.createPurchase(formData);
      onSuccess();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to purchase advertisement:', apiError);
      setError(apiError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Purchase Advertisement</h2>
              <p className="text-sm text-gray-600 mt-1">
                {pkg.name} - {formatPrice(pkg.price_cents, pkg.currency)} for {pkg.duration_days} days
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Vendor Selection */}
            {vendors.length > 1 && (
              <div>
                <label htmlFor="vendor-profile" className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor Profile *
                </label>
                <select
                  id="vendor-profile"
                  required
                  value={formData.vendor_id || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, vendor_id: parseInt(e.target.value) })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select vendor</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.business_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Ad Title */}
            <div>
              <label htmlFor="ad-title" className="block text-sm font-medium text-gray-700 mb-1">
                Advertisement Title *
              </label>
              <input
                id="ad-title"
                type="text"
                required
                maxLength={255}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., Summer Sale - 20% Off!"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="ad-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="ad-description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="Brief description of your advertisement..."
              />
            </div>

            {/* Ad Type */}
            <div>
              <label htmlFor="ad-type" className="block text-sm font-medium text-gray-700 mb-1">
                Advertisement Type *
              </label>
              <select
                id="ad-type"
                required
                value={formData.ad_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ad_type: e.target.value as 'youtube' | 'etsy' | 'direct' | 'custom'
                  })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
              >
                <option value="custom">Custom</option>
                <option value="youtube">YouTube</option>
                <option value="etsy">Etsy</option>
                <option value="direct">Direct Link</option>
              </select>
            </div>

            {/* Target URL */}
            <div>
              <label htmlFor="target-url" className="block text-sm font-medium text-gray-700 mb-1">
                Target URL *
              </label>
              <input
                id="target-url"
                type="url"
                required
                maxLength={500}
                value={formData.target_url}
                onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">
                URL where users will be directed when they click your ad
              </p>
            </div>

            {/* Image URL */}
            <div>
              <label htmlFor="image-url" className="block text-sm font-medium text-gray-700 mb-1">
                Image URL
              </label>
              <input
                id="image-url"
                type="url"
                maxLength={500}
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="https://..."
              />
            </div>

            {/* Display Text */}
            <div>
              <label htmlFor="display-text" className="block text-sm font-medium text-gray-700 mb-1">
                Display Text
              </label>
              <input
                id="display-text"
                type="text"
                maxLength={255}
                value={formData.display_text || ''}
                onChange={(e) => setFormData({ ...formData, display_text: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                placeholder="Call-to-action text"
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Order Summary</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Package:</span>
                  <span className="font-medium text-gray-900">{pkg.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Duration:</span>
                  <span className="font-medium text-gray-900">{pkg.duration_days} days</span>
                </div>
                <div className="flex justify-between">
                  <span>Priority Boost:</span>
                  <span className="font-medium text-gray-900">+{pkg.priority_boost}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="font-medium">Total:</span>
                  <span className="font-bold text-lg text-gray-900">
                    {formatPrice(pkg.price_cents, pkg.currency)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50 flex items-center"
              >
                {submitting ? 'Processing...' : 'Purchase Advertisement'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
